import { Alert, Button, Card, Group, List, Stack, Text, Title } from '@mantine/core';
import { IconClock, IconLifebuoy, IconSettings } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { usePermissions } from '../../lib/permissions';

/**
 * What a vendor sees when the *platform's* Meta setup is incomplete.
 *
 * This exists to fix an audience bug. The diagnosis behind it is good — the API
 * distinguishes "no Meta app" from "no Embedded Signup config" — but today it
 * is rendered to the vendor, along with instructions to open Central, which is
 * the SuperAdmin console they have no access to. So the one person who cannot
 * act gets the actionable detail, and the one who can gets nothing.
 *
 * Here the vendor gets a plain sentence, a route to a human, and the things
 * they *can* still do. The exact missing key renders only for SuperAdmins, who
 * can go and set it.
 */
export interface BlockedByPlatformProps {
  reason: 'no_app' | 'no_config_id';
}

const SUPERADMIN_DETAIL: Record<BlockedByPlatformProps['reason'], string> = {
  no_app: 'No platform Meta app is configured — META_APP_ID / META_APP_SECRET are unset.',
  no_config_id: 'The Embedded Signup config ID (embedded_signup_config_id) is not set.',
};

export function BlockedByPlatform({ reason }: BlockedByPlatformProps) {
  const { isSuperAdmin } = usePermissions();

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <IconClock size={22} />
          <Stack gap={4}>
            <Title order={4}>WhatsApp setup isn&apos;t available yet</Title>
            <Text c="dimmed" size="sm">
              Your provider is still finishing their Meta setup. This isn&apos;t something you can
              fix from here — we&apos;ll let you know as soon as it&apos;s ready.
            </Text>
          </Stack>
        </Group>

        <Group>
          <Button
            variant="light"
            leftSection={<IconLifebuoy size={16} />}
            component="a"
            href="mailto:support@leadsync.app?subject=WhatsApp%20setup%20not%20available"
          >
            Contact support
          </Button>
        </Group>

        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Meanwhile you can still:
          </Text>
          <List size="sm" spacing={2}>
            <List.Item>
              <Text component={Link} to="/vendor/contacts/import" size="sm" c="blue">
                Import your contacts
              </Text>
            </List.Item>
            <List.Item>
              <Text component={Link} to="/vendor/templates" size="sm" c="blue">
                Write message templates
              </Text>
            </List.Item>
          </List>
        </Stack>

        {/* Only rendered for the one role that can act on it. */}
        {isSuperAdmin && (
          <Alert
            color="orange"
            variant="light"
            icon={<IconSettings size={16} />}
            title="Platform setup incomplete"
          >
            <Stack gap="xs" align="flex-start">
              <Text size="sm">{SUPERADMIN_DETAIL[reason]}</Text>
              <Button size="xs" variant="default" component={Link} to="/central/configuration">
                Open Central configuration
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
